import { useEffect, useState, useCallback } from "react";
import apiClient from "../../../../helpers/api-client";
import { SignatureType } from "@verity/database/prisma";

export default function IdPersonSignaturePhoto({
	uuid,
	setReportReady,
	type,
	signatureType,
}: {
	uuid: string | undefined;
	setReportReady?: React.Dispatch<
		React.SetStateAction<{
			roomScan: boolean;
			events: boolean;
			photo: boolean;
		}>
	>;
	type: "id" | "signature" | "both";
	signatureType?: SignatureType | null;
}) {
	// State for storing blob URLs
	const [personPhotoUrl, setPersonPhotoUrl] = useState<string | undefined>(undefined);
	const [idPhotoUrl, setIdPhotoUrl] = useState<string | undefined>(undefined);
	const [signaturePhotoUrl, setSignaturePhotoUrl] = useState<string | undefined>(undefined);

	// Function to load image as blob and return URL
	const loadImageAsBlob = useCallback(async (type: "id" | "signature" | "person") => {
		try {
			if (!uuid) {
				console.warn("No session id");
				return undefined;
			}
			return apiClient.proctoringSession.getPhotoUrl(uuid, type);
		} catch (error) {
			console.error("Error loading image as blob", error);
			return undefined;
		}
	}, []);

	useEffect(() => {
		void (async () => {
			if (uuid) {
				if (type !== "signature") {
					const personPhoto = await loadImageAsBlob("person");
					setPersonPhotoUrl(personPhoto);
					const idPhoto = await loadImageAsBlob("id");
					setIdPhotoUrl(idPhoto);
				}

				if (type !== "id") {
					const signaturePhoto = await loadImageAsBlob("signature");
					setSignaturePhotoUrl(signaturePhoto);
				}
				setReportReady?.((prev) => ({ ...prev, photo: true }));
			}
		})();

		// Cleanup function to revoke URLs
		return () => {
			if (personPhotoUrl) URL.revokeObjectURL(personPhotoUrl);
			if (idPhotoUrl) URL.revokeObjectURL(idPhotoUrl);
			if (signaturePhotoUrl) URL.revokeObjectURL(signaturePhotoUrl);
		};
		// Ensure useEffect reacts to uuid changes, as well as any new URLs generated
	}, [uuid, loadImageAsBlob, setReportReady]);

	const containerHeight = type === "both" ? "690px" : type === "id" ? "320px" : "370px";

	function IdPhotoComponent() {
		return (
			<>
				<div style={{ borderBottom: "1px solid #C7C7C7" }}>
					{" "}
					<strong>ID Verification</strong>
				</div>
				<div
					style={{
						display: "inline",
						flexWrap: "wrap",
					}}
				>
					<div style={{ marginTop: "20px", float: "left" }}>
						<img
							style={{ width: "400px", height: "270px", borderRadius: "4px" }}
							src={personPhotoUrl}
							alt="Photo taken of Candidate"
						/>
					</div>
					<div style={{ marginTop: "20px", float: "right" }}>
						<img
							style={{ width: "400px", height: "270px", borderRadius: "4px" }}
							src={idPhotoUrl}
							alt="Photo of Candidate's ID"
						/>
					</div>
				</div>
			</>
		);
	}

	function SignaturePhotoComponent() {
		return (
			<>
				<div style={{ borderBottom: "1px solid #C7C7C7" }}>
					{" "}
					<strong>Signature</strong>
				</div>
				<div
					style={{
						marginTop: "20px",
						display: "flex",
						justifyContent: "flex-start",
					}}
				>
					<img
						style={{
							width: "480px",
							height: "320px",
							...(signatureType === "drawn" && {
								border: "2px solid #6f6f6f",
								borderRadius: "4px",
							}),
						}}
						src={signaturePhotoUrl}
						alt="Signature"
					/>
				</div>
			</>
		);
	}

	return (
		<div
			style={{
				margin: "40px 40px 0px 40px",
				height: containerHeight,
				color: "#202124",
				display: "flex",
				flexDirection: "column",
				gap: "10px",
			}}
		>
			{type !== "signature" && <IdPhotoComponent />}
			{type !== "id" && <SignaturePhotoComponent />}
		</div>
	);
}
